import { useLoaderData } from '@remix-run/react';
import { json, LoaderArgs } from '@shopify/remix-oxygen';
import { useMemo } from 'react';
import { convertProducts } from '~/graphql/converters/product';
import { GET_PRODUCTS } from '~/graphql/queries/products';
import HomeFlashSale from '~/sections/home/HomeFlashSale';
import { ScratchDrop } from '~/sections/scratchDrop';
import { HomeHero } from '~/sections/home/HomeHero';
import { SignupModal } from '~/components/SignupModal';
import { BRAND, SEO, FALLBACK_SITE_URL } from '~/common/constants/seo';
import {
  landingVideos,
  LANDING_VIDEO_THUMBNAIL,
} from '~/common/constants/video';
import { ProductNode } from '~/types/dto/productDto';
import {
  excludeGatedEdges,
  GATED_PRODUCTS_EXCLUDE_QUERY,
} from '~/common/constants/privateSale';
import { excludeSecretEventEdges } from '~/common/constants/secretEvent';
import {
  getScratchDropConfig,
  isScratchDropEnabled,
} from '~/lib/scratchDrop.server';
import { isShopHomepage, loadShop } from '~/lib/shop.server';
import ShopPage from './shop';

export async function loader(args: LoaderArgs) {
  const { storefront, env } = args.context;

  if (isShopHomepage(env)) {
    return json({ ...(await loadShop(args)), isShopHomepage: true });
  }

  const siteUrl = env.PUBLIC_SITE_URL || FALLBACK_SITE_URL;

  const isScratchDrop = isScratchDropEnabled(env);
  const isFlashSale = env.PUBLIC_ENABLE_FLASH_SALE === 'true';

  // Both campaigns own `/`. Scratch wins, and the collision is logged rather than resolved
  // silently: `app/layout/index.tsx` reads PUBLIC_ENABLE_FLASH_SALE independently from
  // `window.ENV`, so with both set the scratch page renders but the layout still drops the
  // Sidebar (which the scratch design shows) and still force-redirects every non-allow-listed
  // route to `/`. Leaving last campaign's flag on is exactly the kind of thing that happens
  // during an eight-drop day.
  if (isScratchDrop && isFlashSale) {
    console.error(
      '[scratchDrop] PUBLIC_ENABLE_SCRATCH_DROP and PUBLIC_ENABLE_FLASH_SALE are both set. They are mutually exclusive; rendering the scratch drop. Clear PUBLIC_ENABLE_FLASH_SALE — while it is set the Sidebar is hidden and other routes redirect to /.',
    );
  }

  if (!isScratchDrop && !isFlashSale) {
    return json({
      products: null,
      isFlashSale: false,
      isScratchDrop: false,
      scratchDrop: null,
      siteUrl,
    });
  }

  const { products } = await storefront.query(GET_PRODUCTS, {
    variables: {
      query: GATED_PRODUCTS_EXCLUDE_QUERY,
      first: 200,
    },
    cache: storefront.CacheShort(),
  });

  // Both gated product sets, matching /shop and the sitemap. `/` is a publicly
  // attributable surface, so an unreleased product rendered here would also publish its
  // name into an undeletable Klaviyo metric.
  //
  // Private sale is a defensive second pass behind the query's `-tag:` argument, and is
  // free because `tags` is already in the selection. The secret-event product has no such
  // first layer: it is gated by HANDLE, so no `-tag:` argument can exclude it at source and
  // this filter is the only thing keeping it off the homepage. Both takeovers shipped
  // without it.
  const edges = excludeSecretEventEdges(
    excludeGatedEdges<ProductNode>(products.edges),
    env.PUBLIC_SECRET_EVENT_PRODUCT_HANDLE?.trim(),
  );

  return json({
    products: { ...products, edges },
    // Scratch takes precedence, so the flash sale is reported as off when both are set.
    isFlashSale: isFlashSale && !isScratchDrop,
    isScratchDrop,
    scratchDrop: isScratchDrop ? getScratchDropConfig(env, edges) : null,
    siteUrl,
  });
}

export const meta = ({ data }: { data: any }) => {
  const siteUrl = data?.siteUrl || FALLBACK_SITE_URL;

  // Organization structured data
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: BRAND.name,
    url: siteUrl,
    logo: `${siteUrl}${SEO.defaultOgImage}`,
    description: SEO.defaultDescription,
    sameAs: Object.values(BRAND.socials),
    contactPoint: {
      '@type': 'ContactPoint',
      email: BRAND.supportEmail,
      contactType: 'customer service',
    },
  };

  // WebSite structured data with search action
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: BRAND.name,
    url: siteUrl,
    description: SEO.defaultDescription,
    publisher: {
      '@type': 'Organization',
      name: BRAND.name,
    },
  };

  // ClothingStore structured data
  const storeSchema = {
    '@context': 'https://schema.org',
    '@type': 'ClothingStore',
    name: BRAND.name,
    url: siteUrl,
    description: SEO.defaultDescription,
    priceRange: BRAND.priceRange,
    address: {
      '@type': 'PostalAddress',
      addressLocality: BRAND.location.city,
      addressCountry: BRAND.location.country,
    },
  };

  // BreadcrumbList for homepage
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
    ],
  };

  // VideoObject schema for homepage hero video
  const videoSchema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: `${BRAND.name} - Sustainable Streetwear Collection`,
    description: SEO.defaultDescription,
    thumbnailUrl: LANDING_VIDEO_THUMBNAIL,
    uploadDate: new Date().toISOString(),
    contentUrl: landingVideos.mp4,
    embedUrl: siteUrl,
    publisher: {
      '@type': 'Organization',
      name: BRAND.name,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}${SEO.defaultOgImage}`,
      },
    },
  };

  return [
    { title: SEO.defaultTitle },
    { name: 'description', content: SEO.defaultDescription },
    { tagName: 'link', rel: 'canonical', href: siteUrl },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: siteUrl },
    {
      property: 'og:title',
      content: SEO.defaultTitle,
    },
    { property: 'og:description', content: SEO.defaultDescription },
    { property: 'og:image', content: `${siteUrl}${SEO.defaultOgImage}` },
    { property: 'og:image:width', content: String(SEO.defaultOgImageWidth) },
    { property: 'og:image:height', content: String(SEO.defaultOgImageHeight) },
    { property: 'og:image:alt', content: SEO.defaultOgImageAlt },
    { property: 'og:site_name', content: SEO.brandName },
    { name: 'twitter:card', content: SEO.twitterCard },
    {
      name: 'twitter:title',
      content: SEO.defaultTitle,
    },
    { name: 'twitter:description', content: SEO.defaultDescription },
    { name: 'twitter:image', content: `${siteUrl}${SEO.defaultOgImage}` },
    { name: 'referrer', content: 'strict-origin-when-cross-origin' },
    {
      'script:ld+json': JSON.stringify([
        organizationSchema,
        websiteSchema,
        storeSchema,
        breadcrumbSchema,
        // No hero video while the grid owns `/`; schema must match what is on the page.
        ...(data?.isShopHomepage ? [] : [videoSchema]),
      ]),
    },
  ];
};

const Homepage = () => {
  const data = useLoaderData();

  const products = useMemo(() => {
    return data?.products ? convertProducts(data.products.edges) : [];
  }, [data?.products]);

  const scratchDrop = data?.scratchDrop;

  // The shop's own sign-up card states already are a newsletter form; no second one on top.
  if (data?.isShopHomepage) {
    return (
      <>
        <ShopPage />
        {data.isSignUpSurface ? null : <SignupModal />}
      </>
    );
  }

  const mainContent =
    data?.isScratchDrop && scratchDrop ? (
      <ScratchDrop
        products={products}
        designId={scratchDrop.designId}
        countdownTitle={scratchDrop.countdownTitle}
        countdownAt={scratchDrop.countdownAt}
      />
    ) : data?.isFlashSale ? (
      <HomeFlashSale products={products} />
    ) : (
      <HomeHero />
    );

  return (
    <>
      {mainContent}
      {/* Suppressed during a scratch drop: on a single-screen 375px layout the newsletter
          pop-up lands on top of the product card at the drop's peak minute. It writes to
          the Scratch Drop Klaviyo list either way, so this is a UX call, not a data one —
          flagged for Lith rather than assumed permanent. */}
      {data?.isScratchDrop ? null : <SignupModal />}
    </>
  );
};

export default Homepage;
